/**
 * Chart to PDF conversion utilities
 * Since @react-pdf/renderer doesn't support recharts directly,
 * we need to create alternative visualizations or capture charts as images
 */

/**
 * Generate a horizontal bar chart representation using text/symbols
 * @param {Array} data - Chart data
 * @param {string} valueKey - Key for the value field
 * @param {string} nameKey - Key for the name field  
 * @param {number} maxBarLength - Maximum bar length in characters
 * @returns {Array} Text-based chart representation
 */
export const generateTextChart = (data, valueKey = 'cost', nameKey = 'name', maxBarLength = 20) => {
  if (!data || data.length === 0) return [];
  
  // Find the maximum value for scaling
  const maxValue = Math.max(...data.map(item => item[valueKey] || 0));
  
  return data.map(item => {
    const value = item[valueKey] || 0;
    const percentage = maxValue > 0 ? (value / maxValue) : 0;
    const barLength = Math.round(percentage * maxBarLength);
    const bar = '█'.repeat(barLength) + '░'.repeat(maxBarLength - barLength);
    
    return {
      name: item[nameKey],
      value: value,
      formattedValue: typeof value === 'number' ? 
        (value >= 1000 ? `$${(value/1000).toFixed(0)}k` : `$${value}`) : 
        value,
      bar: bar,
      percentage: (percentage * 100).toFixed(1)
    };
  });
};

/**
 * Generate cost comparison data for PDF display
 * @param {Array} contractors - Array of contractor data
 * @returns {Array} Formatted cost comparison data
 */
export const generateCostComparisonForPDF = (contractors) => {
  if (!contractors || contractors.length === 0) return [];
  
  const costData = contractors.map(contractor => {
    const costStr = contractor.payment_terms?.total_cost?.toString() || '0';
    const cleanCost = costStr.replace(/[\$,]/g, '');
    const cost = parseFloat(cleanCost) || 0;
    
    return {
      name: contractor.contractor.contractor_name,
      cost: cost
    };
  });
  
  // Sort by cost (lowest to highest)
  costData.sort((a, b) => a.cost - b.cost);
  
  return generateTextChart(costData, 'cost', 'name');
};

/**
 * Generate allowances comparison data for PDF
 * @param {Array} contractors - Array of contractor data
 * @returns {Object} Allowances comparison data
 */
export const generateAllowancesComparisonForPDF = (contractors) => {
  if (!contractors || contractors.length === 0) return { categories: [], totals: [] };
  
  // Get all allowance categories
  const categories = new Set();
  contractors.forEach(contractor => {
    if (contractor.allowances?.direct_costs) {
      Object.keys(contractor.allowances.direct_costs).forEach(key => {
        categories.add(key);
      });
    }
  });
  
  // Generate comparison data for each category
  const categoryData = Array.from(categories).slice(0, 5).map(category => {
    const contractorValues = contractors.map(contractor => {
      const value = contractor.allowances?.direct_costs?.[category] || 0;
      const cleanValue = typeof value === 'string' ? parseFloat(value.replace(/[\$,]/g, '')) : value;
      return {
        contractor: contractor.contractor.contractor_name,
        value: cleanValue || 0
      };
    });
    
    // Sort by value
    contractorValues.sort((a, b) => b.value - a.value);
    
    return {
      category: category.replace(/_/g, ' '),
      contractors: contractorValues,
      chart: generateTextChart(contractorValues, 'value', 'contractor', 15)
    };
  });
  
  // Calculate totals per contractor
  const totals = contractors.map(contractor => {
    const total = Object.values(contractor.allowances?.direct_costs || {}).reduce((sum, val) => {
      const numVal = typeof val === 'string' ? parseFloat(val.replace(/[\$,]/g, '')) : val;
      return sum + (numVal || 0);
    }, 0);
    
    return {
      contractor: contractor.contractor.contractor_name,
      total: total
    };
  });
  
  return {
    categories: categoryData,
    totals: generateTextChart(totals, 'total', 'contractor')
  };
};

/**
 * Generate contractor score comparison for PDF
 * @param {Array} contractors - Array of contractor data
 * @param {Object} evaluationData - Evaluation data
 * @param {Object} recommendationData - Recommendation data
 * @param {boolean} isUsingGuestData - Whether using guest data format
 * @param {Object} guestAnalysisData - Guest analysis data
 * @returns {Array} Contractor score data
 */
export const generateContractorScoresForPDF = (contractors, evaluationData, recommendationData, isUsingGuestData, guestAnalysisData) => {
  if (!contractors || contractors.length === 0) return [];
  
  const scoreData = contractors.map((contractor, index) => {
    const contractorName = contractor.contractor.contractor_name;
    let overallScore = 0;
    let alignmentScore = 0;
    
    if (isUsingGuestData && guestAnalysisData) {
      // Use guest data format
      const actualData = guestAnalysisData.guestAnalysis || guestAnalysisData;
      const contractorKey = index === 0 ? 'Contractor_1_Score' : 'Contractor_2_Score';
      const contractorScore = actualData?.comparision?.[contractorKey];
      
      if (contractorScore) {
        overallScore = Math.round((contractorScore.Total / 100) * 10); // Convert to 0-10 scale
        alignmentScore = contractorScore.Total || 0;
      }
    } else if (evaluationData) {
      // Use regular evaluation data
      const evaluationKey = `contractor_${index + 1}`;
      const contractorEval = evaluationData[evaluationKey];
      
      if (contractorEval) {
        const categories = [
          'communication_and_accountability',
          'documentation_and_deliverables', 
          'logistics_and_execution_plan',
          'scope_and_methodology',
          'safety_measurements',
          'labor_and_workforce',
          'contracting_behavior'
        ];
        
        const validScores = categories
          .map(cat => contractorEval[cat]?.score)
          .filter(score => score !== undefined && score !== null);
        
        overallScore = validScores.length > 0 ? 
          Math.round(validScores.reduce((sum, score) => sum + score, 0) / validScores.length) : 0;
      }
      
      // Get alignment score from recommendation data
      const recommendationItem = recommendationData?.ranking?.find(
        item => item.contractor_name.toLowerCase().includes(contractorName.toLowerCase().split(' ')[0])
      );
      alignmentScore = recommendationItem?.alignment_score || 0;
    }
    
    return {
      name: contractorName,
      overallScore: overallScore,
      alignmentScore: alignmentScore,
      qualityRating: overallScore >= 8 ? 'Excellent' : overallScore >= 6 ? 'Good' : overallScore >= 4 ? 'Fair' : 'Poor'
    };
  });
  
  return scoreData;
};

/**
 * Generate missing elements analysis for PDF
 * @param {Array} contractors - Array of contractor data
 * @param {Object} evaluationData - Evaluation data
 * @param {boolean} isUsingGuestData - Whether using guest data format
 * @param {Object} guestAnalysisData - Guest analysis data
 * @returns {Array} Missing elements data
 */
export const generateMissingElementsForPDF = (contractors, evaluationData, isUsingGuestData, guestAnalysisData) => {
  if (!contractors || contractors.length === 0) return [];
  
  if (isUsingGuestData && guestAnalysisData) {
    const actualData = guestAnalysisData.guestAnalysis || guestAnalysisData;
    const results = [];
    
    if (actualData?.Contractor_1_Information?.Risk_Alerts) {
      results.push({
        name: 'Contractor 1',
        missing: actualData.Contractor_1_Information.Risk_Alerts.length,
        items: actualData.Contractor_1_Information.Risk_Alerts.map(alert => alert.title).slice(0, 3),
        coverage: 'Basic Coverage'
      });
    }
    
    if (actualData?.Contractor_2_Information?.Risk_Alerts) {
      results.push({
        name: 'Contractor 2',
        missing: actualData.Contractor_2_Information.Risk_Alerts.length,
        items: actualData.Contractor_2_Information.Risk_Alerts.map(alert => alert.title).slice(0, 3),
        coverage: 'Basic Coverage'
      });
    }
    
    return results;
  }
  
  if (!evaluationData) return [];
  
  const results = [];
  Object.keys(evaluationData).forEach(key => {
    if (key.startsWith('contractor_') && evaluationData[key]) {
      const contractor = evaluationData[key];
      const missing = contractor.missing_information || [];
      results.push({
        name: contractor.contractor_name,
        missing: missing.length,
        items: missing.slice(0, 3),
        coverage: contractor.sow_coverage
      });
    }
  });
  
  return results;
};

/**
 * Generate risk assessment data for PDF
 * @param {Array} contractors - Array of contractor data
 * @param {Object} metrics - Key metrics data
 * @returns {Array} Risk assessment data
 */
export const generateRiskAssessmentForPDF = (contractors, metrics) => {
  const risks = [];
  
  // Cost variance risk
  if (metrics.costDifferencePercentage > 20) {
    risks.push({
      type: 'High Cost Variance',
      level: 'High',
      description: `${metrics.costDifferencePercentage}% difference between proposals suggests significant scope variations`,
      recommendation: 'Review exclusions and allowances carefully to ensure fair comparison'
    });
  }
  
  // Scope completeness risk
  risks.push({
    type: 'Scope Completeness',
    level: 'Medium',
    description: 'Different contractors may include varying levels of detail',
    recommendation: 'Request clarifications for incomplete proposals to avoid 10-25% cost increases'
  });
  
  // Market positioning risk
  const avgCost = metrics.averageCost;
  contractors.forEach(contractor => {
    const costStr = contractor.payment_terms?.total_cost?.toString() || '0';
    const cost = parseFloat(costStr.replace(/[\$,]/g, '')) || 0;
    const deviation = ((cost - avgCost) / avgCost * 100).toFixed(1);
    
    if (Math.abs(deviation) > 15) {
      risks.push({
        type: 'Price Outlier',
        level: deviation > 0 ? 'Medium' : 'Low',
        description: `${contractor.contractor.contractor_name} is ${Math.abs(deviation)}% ${deviation > 0 ? 'above' : 'below'} average`,
        recommendation: deviation > 0 ? 'Verify if premium pricing includes additional value' : 'Ensure all required scope is included'
      });
    }
  });
  
  return risks.slice(0, 5); // Limit to top 5 risks
};

/**
 * Calculate key insights for PDF
 * @param {Array} contractors - Array of contractor data
 * @param {Object} metrics - Key metrics data
 * @param {Object} recommendation - Recommendation data
 * @returns {Array} Key insights
 */
export const generateKeyInsightsForPDF = (contractors, metrics, recommendation) => {
  const insights = [];
  
  // Cost analysis insight
  insights.push({
    category: 'Cost Analysis',
    title: 'Price Range Analysis',
    description: `${contractors.length} proposals analyzed with ${metrics.costDifferencePercentage}% variance between highest and lowest bids`,
    impact: 'Financial Planning'
  });
  
  // Recommendation insight
  if (recommendation) {
    insights.push({
      category: 'AI Recommendation',
      title: 'Preferred Contractor',
      description: `${recommendation.recommended_contractor} identified as optimal choice based on comprehensive analysis`,
      impact: 'Decision Making'
    });
  }
  
  // Savings opportunity insight
  if (metrics.maxSavings > 0) {
    insights.push({
      category: 'Savings Opportunity',
      title: 'Potential Savings',
      description: `Up to $${metrics.maxSavings.toLocaleString()} potential savings by choosing the most cost-effective option`,
      impact: 'Budget Optimization'
    });
  }
  
  // Market positioning insight
  const avgCost = metrics.averageCost;
  const marketPosition = avgCost > 100000 ? 'Premium' : avgCost > 50000 ? 'Mid-Range' : 'Budget';
  insights.push({
    category: 'Market Position',
    title: 'Project Positioning',
    description: `Average proposal cost of $${avgCost.toLocaleString()} places this project in the ${marketPosition} market segment`,
    impact: 'Market Understanding'
  });
  
  return insights;
}; 